/* eslint-disable react/no-unstable-nested-components */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { AxiosResponse } from "axios";
import styled from "styled-components";
import dayjs from "dayjs";
import { ViewState } from "@devexpress/dx-react-scheduler";
import {
	Scheduler,
	Appointments,
	MonthView,
	DateNavigator,
	Toolbar,
} from "@devexpress/dx-react-scheduler-material-ui";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";

import { MAIN_THEME } from "styles/colors";
import ReportsClient from "services/api/reports";
import { getPropertiesList } from "state/modules/properties";

import { CHANNEL_NAMES } from "interfaces/channels";

import { Property } from "models/properties";
import { defaultReservationsDateFilter } from "utils/reservations";
import { GenerateReportResponse } from "models/reports";
import { ReportTableItemWithTransfer } from "interfaces/reports";
import { MultiSelect } from "@hotelise/ui";
import { LoaderWithOverlay } from "components/atoms";
import ReservationDetailsPanel from "./ReservationDetailsPanel";

// Keep styled-component for CalendarContainer due to deep CSS overrides
// targeting internal MUI/scheduler class names
const CalendarContainer = styled.div`
	position: relative;
	background: rgba(15, 26, 53, 0.8);
	border: 1px solid rgba(0, 217, 255, 0.15);
	border-radius: 16px;
	overflow: hidden;

	.dx-scheduler-table {
		color: ${MAIN_THEME.TEXT_PRIMARY};
	}
	

	.dx-scheduler-date-table-cell > div {
		color: #ffffff !important;
	}

	.dx-scheduler-date-table-cell > div > div {
		color: #ffffff !important;
	}
	
	
	.dx-scheduler-date-table-cell * {
		color: #f8fafc !important;
	}

	.dx-scheduler-date-table-cell span {
		color: #f8fafc !important;
	}

	[class*="MuiTableCell-root"] {
		color: #f8fafc !important;
	}

	.dx-scheduler-date-table-cell [class*="MuiTableCell"] {
		color: #f8fafc !important;
	}
`;

const SchedulerFork = Scheduler as any;

const theme = createTheme({
	palette: {
		type: "dark",
		primary: {
			main: MAIN_THEME.ACCEPT_COLOR,
		},
		secondary: {
			main: MAIN_THEME.ACCEPT_COLOR,
		},
		background: {
			paper: "rgba(15, 26, 53, 0.95)",
			default: MAIN_THEME.DARK_BG,
		},
		text: {
			primary: MAIN_THEME.TEXT_PRIMARY,
			secondary: MAIN_THEME.TEXT_SECONDARY,
		},
		divider: "rgba(0, 217, 255, 0.15)",
	},
	overrides: {
		MuiPaper: {
			root: {
				backgroundColor: "rgba(15, 26, 53, 0.95)",
				border: "1px solid rgba(0, 217, 255, 0.15)",
				boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
			},
		},
		MuiToolbar: {
			root: {
				backgroundColor: "rgba(15, 26, 53, 0.95)",
				borderBottom: "1px solid rgba(0, 217, 255, 0.1)",
			},
		},
		MuiIconButton: {
			root: {
				color: MAIN_THEME.ACCEPT_COLOR,
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
				},
			},
			colorPrimary: {
				color: MAIN_THEME.ACCEPT_COLOR,
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
					color: MAIN_THEME.ACCEPT_COLOR,
				},
			},
		},
		MuiButtonBase: {
			root: {
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
				},
			},
		},
		MuiButton: {
			text: {
				"&:hover": {
					backgroundColor: "rgba(0, 217, 255, 0.12)",
				},
			},
		},
		MuiTypography: {
			body1: {
				color: MAIN_THEME.TEXT_PRIMARY,
			},
			body2: {
				color: MAIN_THEME.TEXT_PRIMARY,
			},
			h6: {
				color: MAIN_THEME.TEXT_PRIMARY,
				fontWeight: 600,
				letterSpacing: "0.3px",
			},
		},
		MuiTable: {
			root: {
				backgroundColor: "transparent",
			},
		},
		MuiTableHead: {
			root: {
				backgroundColor: "rgba(15, 26, 53, 0.6)",
			},
		},
		MuiTableCell: {
			root: {
				color: MAIN_THEME.TEXT_PRIMARY,
				borderColor: "rgba(0, 217, 255, 0.1)",
			},
			head: {
				color: MAIN_THEME.TEXT_SECONDARY,
				fontWeight: 600,
				fontSize: "13px",
				letterSpacing: "0.3px",
			},
			body: {
				color: MAIN_THEME.TEXT_PRIMARY,
				fontSize: "14px",
				padding: "12px 8px",
			},
		},
	},
});

const ReservationsCalendar = () => {
	const properties = useSelector(getPropertiesList);
	const [events, setEvents] = useState<any[]>([]);
	const [isLoading, setLoading] = useState(false);
	const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>(["all"]);
	const [selectedReservation, setSelectedReservation] =
		useState<ReportTableItemWithTransfer | null>(null);

	const onPageLoading = useCallback(
		async (date: Date | string, propertyId?: string) => {
			if (!properties.length) return;

			setLoading(true);
			setEvents([]);

			const dateFrom = dayjs(date).startOf("month").toISOString();
			const dateTo = dayjs(date).endOf("month").toISOString();

			try {
				const propertyIds = properties.map((item) => item.id);
				const propertyIdsString = propertyIds.join(",");

				const propertiesMap = {} as {
					[n: string]: Property;
				};

				properties.forEach((property) => {
					propertiesMap[property.id] = property;
				});

				let filteredPropertyIds = propertyIdsString;
				if (propertyId) {
					filteredPropertyIds = propertyId;
				} else if (selectedPropertyIds?.length && !selectedPropertyIds.includes("all")) {
					filteredPropertyIds = selectedPropertyIds.join(",");
				}

				const reportsRes = (await ReportsClient.generateReport({
					propertyIds: filteredPropertyIds,
					dateFrom,
					dateTo,
					dateFromType: defaultReservationsDateFilter.dateFromType,
					dateToType: defaultReservationsDateFilter.dateToType,
				})) as AxiosResponse<GenerateReportResponse>;

				const transformedReservations =
					reportsRes.data.content.calculations.allAccountTransfers.map(
						(item) => ({
							...item,
							property: propertiesMap[item._item.propertyId] || "",
						})
					);

				const updatedEvents = transformedReservations.map((reservation) => {
					let color = "rgb(255, 121, 38)";

					switch (reservation._item.channel) {
						case CHANNEL_NAMES.AIRBNB_OFFICIAL:
						case CHANNEL_NAMES.AIRBNB:
							color = "#FF385C";

							break;
						case CHANNEL_NAMES.BOOKING:
							color = "#02357F";

							break;
						default:
							color = "rgb(255, 121, 38)";

							break;
					}

					return {
						id: reservation._item.channel,
						title: `${reservation._item.guest.first_name} ${reservation._item.guest.last_name}`,
						startDate: reservation._item.start_date,
						endDate: reservation._item.end_date,
						color,
						reservation,
						channel: reservation._item.channel,
					};
				});

				setEvents(updatedEvents);
				setLoading(false);
			} catch (error) {
				console.log({ error });
				setLoading(false);
			}
		},
		[properties, selectedPropertyIds]
	);

	useEffect(() => {
		onPageLoading(dayjs().toISOString());
	}, [properties]);

	const transformedProperties = useMemo(
		() =>
			properties.map((item) => ({
				id: item.id,
				label: item.name,
			})),
		[properties]
	);

	const handleSelectReservation = (
		item: ReportTableItemWithTransfer | null
	) => {
		setSelectedReservation(item);
	};

	const handleSelectProperties = (selected: string[]) => {
		setSelectedPropertyIds(selected.includes("all") ? ["all"] : selected);
		onPageLoading(dayjs().toISOString());
	};

	const Appointment = ({ children, style, data, ...restProps }: any) => {
		return (
			<Appointments.Appointment
				{...restProps}
				onClick={() => handleSelectReservation(data.reservation)}
				style={{
					...style,
					backgroundColor: data.color,
					borderRadius: "8px",
				}}
			>
				{children}
			</Appointments.Appointment>
		);
	};

		return (
			<div className="relative">
				<div className="relative z-[11] mb-7">
					<MultiSelect
						items={[{ id: "all", label: "All Properties" }, ...transformedProperties]}
						selectedItems={selectedPropertyIds}
						onSelectionChange={handleSelectProperties}
						placeholder="Select properties..."
						renderSelectedTags={false}
					/>
				</div>
			<CalendarContainer>
				<MuiThemeProvider theme={theme}>
					<SchedulerFork
						data={events}
						style={{
							background: "transparent",
						}}
					>
						<ViewState onCurrentDateChange={onPageLoading} />
						<Toolbar />
						<DateNavigator />
						<MonthView />
						<Appointments
							appointmentComponent={Appointment}
						/>
					</SchedulerFork>
				</MuiThemeProvider>
				{isLoading ? <LoaderWithOverlay /> : null}
			</CalendarContainer>
			<ReservationDetailsPanel
				reservation={selectedReservation}
				open={!!selectedReservation}
				onOpenChange={(open) => {
					if (!open) setSelectedReservation(null);
				}}
			/>
		</div>
	);
};

export default ReservationsCalendar;
